import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';

export enum DOCUMENT_TYPE {
	PDF = 'PDF',
	IMAGE = 'IMAGE',
}

export enum ExcelFormat {
	CSV = 'csv',
	XLSX = 'xlsx',
}

export const PDF_EXTENSION = 'pdf';

export const ACCEPT_FILES_TYPES = {
	IMAGE_AND_PDF: 'image/png,image/jpeg,application/pdf',
	PDF: 'application/pdf',
};

export const ACCEPT_FILE_EXTENSIONS = {
	IMAGE_AND_PDF: ['png', 'jpeg', 'pdf'],
	PDF: ['pdf'],
};

export const IMAGE_EXTENSIONS: FileExtension[] = [
	FileExtension.png,
	FileExtension.jpg,
	FileExtension.jpeg,
	FileExtension.gif,
	FileExtension.webp,
	FileExtension.svg,
	FileExtension.bmp,
	FileExtension.tiff,
];

const MIME_TO_FILE_EXTENSION: Record<string, FileExtension> = {
	'application/pdf': FileExtension.pdf,
	'image/png': FileExtension.png,
	'image/jpeg': FileExtension.jpg,
	'application/vnd.ms-excel': FileExtension.xls,
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileExtension.xlsx,
};

export const getFileExtensionFromMimeType = (
	contentType: string,
	fallback: FileExtension = FileExtension.pdf
): FileExtension => {
	return MIME_TO_FILE_EXTENSION[contentType] ?? fallback;
};

/**
 * Reverse of {@link MIME_TO_FILE_EXTENSION}: resolve a MIME type from a file
 * extension. Used to tag streamed S3 uploads (which otherwise default to
 * application/octet-stream) with a correct Content-Type. Returns undefined when
 * the extension isn't in the known map, so callers can omit ContentType rather
 * than guess wrong.
 */
export const getMimeTypeFromExtension = (extension: string): string | undefined => {
	const normalized = extension.replace(/^\./, '').toLowerCase();
	if (!normalized) {
		return undefined;
	}
	return Object.entries(MIME_TO_FILE_EXTENSION).find(([, ext]) => ext === normalized)?.[0];
};

export const isImageExtension = (fileExtension: FileExtension) => {
	return IMAGE_EXTENSIONS.includes(fileExtension);
};

export const hasValidExtension = (value: string) => {
	const dotPosition = value.lastIndexOf('.');
	if (dotPosition === -1) {
		return false;
	}
	// Extensions are stored lowercase in the enum; compare case-insensitively so
	// filenames like `contract.PDF` are recognised as already having an extension.
	const extension = value.slice(dotPosition + 1).toLowerCase();
	return extension in FileExtension;
};

export const getValidFileNameForDownload = (fileName: string, fileExtension: FileExtension) => {
	if (hasValidExtension(fileName)) {
		return fileName;
	}

	return `${fileName}.${fileExtension}`;
};

// removes the file extension if any, prior to appendage of any text
export const removeFileExtension = (filename: string) => {
	const dotPosition = filename.lastIndexOf('.');
	return dotPosition === -1 ? filename : filename.slice(0, dotPosition);
};

// adds the suffixString to the filename, then appends file extension after it
export const formatFileNameAppend = (
	filename: string,
	suffixString: string,
	fileExtension: FileExtension
) => {
	return hasValidExtension(filename)
		? `${removeFileExtension(filename)}${suffixString}.${fileExtension}`
		: `${filename}${suffixString}.${fileExtension}`;
};

export const appendToFileName = (filename: string, suffix: string): string => {
	const dotPosition = filename.lastIndexOf('.');
	if (dotPosition === -1) {
		return `${filename}${suffix}`;
	}
	const baseName = filename.slice(0, dotPosition);
	const extension = filename.slice(dotPosition);
	return `${baseName}${suffix}${extension}`;
};

// Derive a user-facing document name from a file name.
// Strips the extension, sanitises characters via cleanFileName (keeping the casing), and replaces any
// remaining '.' with '-' so the result passes the contractName yup rule (rejects . ? * : / " < > | \).
export const fileNameToDocumentName = (fileName: string): string => {
	return removeFileExtension(cleanFileName(fileName, { preserveCase: true })).replace(/\./g, '-');
};

// Lowercased by default: the result is also the S3 key and the signed upload Content-Disposition.
export const cleanFileName = (fileName: string, { preserveCase = false } = {}) => {
	const extensionMatch = fileName.match(/\.[^/.]+$/);
	const extension = extensionMatch ? extensionMatch[0] : '';

	let baseName = fileName.replace(extension, '');

	// Replace unsafe characters (except spaces) with '-'
	baseName = baseName
		.replace(/[^a-zA-Z0-9._\- ]/g, '-') // keep spaces
		.replace(/-+/g, '-') // collapse multiple '-' into one
		.replace(/^\-+/, '') // remove leading '-'
		.replace(/\-+$/, ''); // remove trailing '-'

	if (!preserveCase) {
		baseName = baseName.toLowerCase();
	}

	// Truncate
	const maxFileNameLength = 931 - extension.length;
	if (baseName.length > maxFileNameLength) {
		baseName = baseName.substring(0, maxFileNameLength);
	}

	return `${baseName}${extension}`;
};
